import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';
import { studentPaymentApi } from './StudentPaymentApi';

export const studentsApi = createApi({
  reducerPath: 'studentsApi',
  baseQuery,
  tagTypes: ['Student', 'Subscriptions', 'Exemptions'],
  endpoints: builder => ({
    getStudents: builder.query<
      any,
      { search: string; classId: string; status?: string; itemId?: string }
    >({
      query: ({ search, classId, status, itemId }) => {
        const queryParams = new URLSearchParams();

        if (search) {
          queryParams.append('search', search);
        }

        if (classId) {
          queryParams.append('class', classId);
        }

        if (status) {
          queryParams.append('status', status);
        }

        if (itemId) {
          queryParams.append('itemId', itemId);
        }

        const queryString = queryParams.toString()
          ? `?${queryParams.toString()}`
          : '';

        return `/enrollment/students${queryString}`;
      },
      providesTags: ['Student'],
    }),

    getStudent: builder.query<any, string>({
      query: id => `/enrollment/students/${id}`,
      providesTags: ['Student'],
    }),
    createStudent: builder.mutation<any, any>({
      query: newStudent => ({
        url: '/enrollment/students',
        method: 'POST',
        body: newStudent,
      }),
      invalidatesTags: ['Student'],
    }),
    updateStudent: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/enrollment/students/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['Student'],
    }),
    deleteStudent: builder.mutation<void, number>({
      query: id => ({
        url: `/enrollment/students/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Student', id }, 'Student'],
    }),
    getRelatives: builder.query<any, { search: string }>({
      query: ({ search }) => {
        const queryParams: string[] = [];

        if (search) {
          queryParams.push(`q=${encodeURIComponent(search)}`);
        }

        const queryString = queryParams.length ? `?${queryParams}` : '';

        return `/enrollment/parents${queryString}`;
      },
      providesTags: ['Student'],
    }),
    deleteStudentRelative: builder.mutation<
      void,
      { studentId: number; relativeId: number }
    >({
      query: ({ studentId, relativeId }) => ({
        url: `/enrollment/students/${studentId}/relatives/${relativeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Student'],
    }),
    updateStudentClass: builder.mutation<
      any,
      { studentId: string; newClassId: string }
    >({
      query: ({ studentId, newClassId }) => ({
        url: `/enrollment/students/${studentId}/new-class/${newClassId}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Student'],
    }),
    getStudentSubscriptions: builder.query<
      any,
      { studentId: string; sessionId?: number }
    >({
      query: ({ studentId, sessionId }) => {
        let url = `/finance/sales/subscriptions?studentId=${studentId}`;
        if (sessionId) url += `&sessionId=${sessionId}`;
        return url;
      },
      providesTags: ['Student', 'Subscriptions'],
    }),
    createStudentSubscription: builder.mutation<any, any>({
      query: ({ studentId, itemId, startDate, frequency }) => ({
        url: `/finance/sales/items/${itemId}/subscribe`,
        method: 'POST',
        body: { studentId, startDate, frequency },
      }),
      invalidatesTags: ['Student', 'Subscriptions'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            studentPaymentApi.util.invalidateTags([
              'studentSummary',
              'studentPayment',
            ])
          );
        } catch (e) {
          console.error(e);
        }
      },
    }),
    cancelStudentSubscription: builder.mutation<any, any>({
      query: ({ subscriptionId }) => ({
        url: `/finance/sales/subscriptions/${subscriptionId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Student', 'Subscriptions'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            studentPaymentApi.util.invalidateTags([
              'studentSummary',
              'studentPayment',
            ])
          );
        } catch (e) {
          console.error(e);
        }
      },
    }),
    updateSubscriptionStartDate: builder.mutation<any, { subscriptionId: string; startDate: string }>({
      query: ({ subscriptionId, startDate }) => ({
        url: `/finance/sales/subscriptions/${subscriptionId}/update-start-date`,
        method: 'PATCH',
        body: { startDate },
      }),
      invalidatesTags: ['Student', 'Subscriptions'],
    }),
    getStudentExamReports: builder.query<any, string>({
      query: studentId => `/academics/students/${studentId}/examinations`,
      providesTags: ['Student'],
    }),
    getSubscriptionExemptions: builder.query<any, { studentId?: string; itemId?: string; startDate?: string; endDate?: string }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.studentId) queryParams.append('student_id', params.studentId);
        if (params.itemId) queryParams.append('item_id', params.itemId);
        if (params.startDate) queryParams.append('start_date', params.startDate);
        if (params.endDate) queryParams.append('end_date', params.endDate);
        return `/finance/sales/subscriptions/exemptions?${queryParams.toString()}`;
      },
      providesTags: ['Exemptions'],
    }),
    createSubscriptionExemption: builder.mutation<any, any>({
      query: exemption => ({
        url: '/finance/sales/subscriptions/exemptions',
        method: 'POST',
        body: exemption,
      }),
      invalidatesTags: ['Exemptions', 'Subscriptions'],
    }),
    deleteSubscriptionExemption: builder.mutation<void, number>({
      query: id => ({
        url: `/finance/sales/subscriptions/exemptions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exemptions', 'Subscriptions'],
    }),
    markStudentRepeater: builder.mutation<void, { studentId: string; reason?: string }>({
      query: ({ studentId, reason }) => ({
        url: `/enrollment/students/${studentId}/repeater`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: ['Student'],
    }),
    unmarkStudentRepeater: builder.mutation<void, string>({
      query: studentId => ({
        url: `/enrollment/students/${studentId}/repeater`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Student'],
    }),
  }),
});

export const {
  useGetStudentsQuery,
  useGetRelativesQuery,
  useGetStudentQuery,
  useCreateStudentMutation,
  useUpdateStudentMutation,
  useDeleteStudentMutation,
  useDeleteStudentRelativeMutation,
  useUpdateStudentClassMutation,
  useGetStudentSubscriptionsQuery,
  useCreateStudentSubscriptionMutation,
  useCancelStudentSubscriptionMutation,
  useUpdateSubscriptionStartDateMutation,
  useGetStudentExamReportsQuery,
  useGetSubscriptionExemptionsQuery,
  useCreateSubscriptionExemptionMutation,
  useDeleteSubscriptionExemptionMutation,
  useMarkStudentRepeaterMutation,
  useUnmarkStudentRepeaterMutation,
} = studentsApi;
